import {Component, EventEmitter, Input, Output} from '@angular/core';
import {MenuItem} from 'primeng/api';
import {firstValueFrom, takeUntil} from 'rxjs';
import {
    ECpmCObjectStoreEntryAttributesProxyApiEnum,
    ECpmCObjectStoreEntryProxyTypeApiEnum
} from '../../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-object-store-entry-proxy.api-enums';
import {CpmCObjectStoreEntryProxyApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-object-store-entry-proxy.api-model';
import {CpmCObjectStoreEntryProxyApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-object-store-entry-proxy.api-service';
import {
    ETableColDataType,
    ETableColFilterType,
    TableTagDef
} from '../../../../sdk/src/components/generic-table/table-col-def';
import {IBaseCRUDComponent} from '../../../../sdk/src/components/ibase-crud/ibase-crud.component';
import {EDeleteMode} from '../../../../sdk/src/enums/delete-mode.enum';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';
import {
    ObjectStoreValueDataCellDisplayComponent,
    ObjectStoreValueDataCellDisplayExtra
} from '../../data-cell-display/object-store-value-cell-display/object-store-value-cell-display.component';
import {
    NewEditObjectStoreEntryModalComponent,
    NewEditObjectStoreEntryModalConfig,
    NewEditObjectStoreEntryModalResult
} from '../../modals/new-edit-object-store-entry-modal/new-edit-object-store-entry-modal.component';
import {
    ObjectStoreEntryHistoryComponent,
    ObjectStoreHistoryModalConfig
} from '../../modals/new-edit-object-store-entry-modal/object-store-entry-history/object-store-entry-history.component';
import {SdkBridge} from '../../sdk-bridge';
import {ObjectStoreEntriesTransientCache} from '../../transient-cache/object-store-entries.transient-cache';

class ObjectStoreEntryProxyModelExtension extends CpmCObjectStoreEntryProxyApiModel {
    public _value_cache?: string;
}

/**
 * ObjectStoreEntryProxy CRUD table component
 */
@Component({
    selector: 'app-object-store-entry-crud',
    templateUrl: './object-store-entry-crud.component.html',
    imports: [PrimeNgImportsModule, SdkImportsModule]
})
export class ObjectStoreEntryCRUDComponent extends IBaseCRUDComponent<
    ObjectStoreEntryProxyModelExtension,
    NewEditObjectStoreEntryModalComponent,
    NewEditObjectStoreEntryModalConfig,
    NewEditObjectStoreEntryModalResult
> {
    protected readonly CpmCObjectStoreEntryProxyApiService = CpmCObjectStoreEntryProxyApiService;

    // ===============================================================
    // ===============================================================

    private cache!: ObjectStoreEntriesTransientCache;

    // ===============================================================
    // ===============================================================

    protected helpVisible = false;
    protected vtypeTags: TableTagDef[] = Object.keys(ECpmCObjectStoreEntryProxyTypeApiEnum).map((x) => ({
        style: {background: this.generateColorFromString(x)},
        label: x,
        value: x
    }));

    // ===============================================================
    // ===============================================================

    @Input()
    public linkObjectType!: string;
    @Input()
    public linkObjectUUID!: string;

    @Output()
    public objectStoreChanged = new EventEmitter<void>();

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure({
            // Model config
            modelName: 'ObjectStoreEntryApiModel',
            getModelHumanIdentifier: (model: CpmCObjectStoreEntryProxyApiModel) =>
                model.key ?? '<Unknown object store entry>',

            // Service config
            deleteMode: EDeleteMode.CONFIRM_REENFORCED,
            deleteModelService: (modelToDelete: CpmCObjectStoreEntryProxyApiModel) =>
                CpmCObjectStoreEntryProxyApiService.deleteObjectStoreEntryProxy(
                    this.linkObjectType,
                    this.linkObjectUUID,
                    modelToDelete.key
                ),

            // Modal config
            modalType: NewEditObjectStoreEntryModalComponent,
            getDialogServiceParameters: NewEditObjectStoreEntryModalComponent.getDialogServiceParameters,
            modalExtendedConfigBuilder: (model?: CpmCObjectStoreEntryProxyApiModel) =>
                new NewEditObjectStoreEntryModalConfig(this.linkObjectType, this.linkObjectUUID, model),

            // Authorization config
            authorizationNew: CpmCObjectStoreEntryProxyApiService.endpointSet(
                this.FAKE_UUID,
                this.linkObjectType,
                this.linkObjectUUID,
                'placeholder'
            ),
            authorizationEdit: CpmCObjectStoreEntryProxyApiService.endpointSet(
                this.FAKE_UUID,
                this.linkObjectType,
                this.linkObjectUUID,
                'placeholder'
            ),
            authorizationDelete: CpmCObjectStoreEntryProxyApiService.endpointDelete(
                this.FAKE_UUID,
                this.linkObjectType,
                this.linkObjectUUID,
                'placeholder'
            )
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */

    protected override async onReady(): Promise<void> {
        await this.globalsService.sessionService.getRouteContextIsReadyPromise();

        this.cache = new ObjectStoreEntriesTransientCache(this.globalsService.sessionService.getCustomer()!.uuid, {
            organizationUUID: this.globalsService.sessionService.getOrganization()?.uuid,
            projectUUID: this.globalsService.sessionService.getProject()?.uuid,
            serviceRequestUUID: this.globalsService.sessionService.getServiceRequest()?.uuid
        });
        await this.cache.init();

        // Subscribe to SDK events and re-emit through our custom @Output
        this.onModalResult.pipe(takeUntil(this._destroy$)).subscribe(() => {
            this.objectStoreChanged.emit();
        });

        this.onDeleteResult.pipe(takeUntil(this._destroy$)).subscribe(() => {
            this.objectStoreChanged.emit();
        });
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On refresh (Error handling is done in the parent)
     */
    protected override async onRefresh(): Promise<void> {
        this.loadingStr = 'Loading entries...';
        const result = await firstValueFrom(
            CpmCObjectStoreEntryProxyApiService.getObjectStoreEntryProxyList(
                this.linkObjectType,
                this.linkObjectUUID,
                {
                    'attributes(bitnot)': ECpmCObjectStoreEntryAttributesProxyApiEnum.INTERNAL
                },
                this.paginatorConfig
            )
        );

        this.items = result.data ?? [];
        this.totalItemCount = result.total;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On help button pressed
     */
    public override async onHelp(): Promise<void> {
        this.helpVisible = true;
    }

    // ===============================================================
    // ===============================================================

    // ===============================================================
    // ===============================================================

    /**
     * Init the columns (Error handling is done in the parent)
     */
    protected override async initColumns(): Promise<void> {
        this.tableCols = [
            {
                field: 'key',
                header: 'Key',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            },
            {
                field: 'vtype',
                header: 'Type',
                dataType: ETableColDataType.TAGS,
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.SELECT,
                filterSelectOptions: this.vtypeTags,
                tags: this.vtypeTags
            },
            {
                field: 'schema',
                header: 'Schema',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            },
            {
                field: 'date',
                header: 'Modified At',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.DATE,
                dataType: ETableColDataType.DATE
            },
            {
                field: 'user_uuid',
                header: 'Modified By',
                dataType: ETableColDataType.DATA_DISPLAY_CELL_COMPONENT,
                dataCellDisplayComponent: SdkBridge.userDataCellDisplayComponent,
                searcheable: false,
                filterable: false
            },
            {
                field: '_value_cache',
                header: 'Value',
                searcheable: false,
                filterable: false,
                dataType: ETableColDataType.DATA_DISPLAY_CELL_COMPONENT,
                dataCellDisplayComponent: ObjectStoreValueDataCellDisplayComponent,
                dataCellDisplayExtra: {
                    linkObjectType: this.linkObjectType,
                    linkObjectUUID: this.linkObjectUUID,
                    forceRefreshData: true,
                    openDialogMode: EDialogOpenMode.READ_ONLY
                } as ObjectStoreValueDataCellDisplayExtra
            }
        ];
    }

    protected override buildDefaultContextMenu(row: ObjectStoreEntryProxyModelExtension): MenuItem[] {
        const menu = super.buildDefaultContextMenu(row);
        menu.push({
            label: 'History',
            icon: 'pi pi-history',
            command: () => this.openHistoryModal(row)
        });
        return menu;
    }

    private openHistoryModal(row: ObjectStoreEntryProxyModelExtension): void {
        this.globalsService.dialogService
            .open(ObjectStoreEntryHistoryComponent, {
                data: new ObjectStoreHistoryModalConfig(row, this.linkObjectType, this.linkObjectUUID)
            })
            .onClose.subscribe((result) => {
                if (result) {
                    this.refresh();
                }
            });
    }
}
